import { Box, Button, TextField } from "@mui/material";
import { useState, useEffect } from "react";
import { useIntl } from "react-intl";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-start",
  },
};

export const formatJson = (jsonString: string): string => {
  if (!jsonString?.trim()) {
    return jsonString;
  }

  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    throw new Error("Invalid JSON format");
  }
};

export const validateJsonObject = (
  value: string,
  t: (params: { id: string }) => string
): string | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value);

    // Check if it's an array
    if (Array.isArray(parsed)) {
      return t({ id: "jsonOnlyObjectsAllowed" });
    }

    // Check if it's an object (and not null, which is also typeof 'object')
    if (typeof parsed !== "object" || parsed === null) {
      return t({ id: "jsonMustBeValidObject" });
    }

    return undefined;
  } catch (error) {
    return t({ id: "invalidJsonFormat" });
  }
};

type SettingJsonFieldProps = {
  value: string;
  error?: boolean;
  helperText?: React.ReactNode;
  onChange: (value: string) => void;
};

export default function SettingJsonField({
  value,
  error,
  helperText,
  onChange,
}: SettingJsonFieldProps) {
  const { formatMessage: t } = useIntl();
  const [localValue, setLocalValue] = useState(value || "");

  // Sync local value with prop value when it changes externally
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleFormat = () => {
    try {
      const formatted = formatJson(localValue);
      setLocalValue(formatted);
      onChange(formatted);
    } catch (err) {
      // Error will be shown through validation
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  return (
    <Box sx={styles.container}>
      <TextField
        variant="standard"
        fullWidth
        multiline
        minRows={4}
        value={localValue}
        onChange={handleChange}
        error={error}
        helperText={helperText}
        placeholder='{"key": "value"}'
      />
      <Box sx={styles.buttonContainer}>
        <Button
          variant="text"
          size="small"
          onClick={handleFormat}
          disabled={!localValue?.trim()}
        >
          {t({ id: "formatJsonButton" })}
        </Button>
      </Box>
    </Box>
  );
}
